if (!customElements.get('newsletter-modal')) {
  customElements.define(
    'newsletter-modal',
    class NewsletterModal extends ModalElement {
      constructor() {
        super();
    
        // Prevent popup on Shopify robot challenge page
        if (window.location.pathname === '/challenge' || !theme.cookiesEnabled()) {
          return;
        }
    
        if (!theme.config.isTouch || Shopify.designMode) {
          this.init();
        }
        else {
          new theme.initWhenVisible(theme.utils.throttle(this.init.bind(this)));
        }
      }
    
      get shouldLock() {
        return true;
      }
    
      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }
    
      get delay() {
        return this.hasAttribute('data-delay') ? parseInt(this.getAttribute('data-delay')) : 5;
      }
    
      get expiry() {
        return this.hasAttribute('data-expiry') ? parseInt(this.getAttribute('data-expiry')) : 30;
      }

      get closeExpiry() {
        return this.hasAttribute('data-close-expiry') ? parseInt(this.getAttribute('data-close-expiry')) : this.expiry;
      }

      get cookieName() {
        return 'concept:newsletter-popup';
      }
    
      get submited() {
        return this.querySelector('.alert') !== null;
      }
    
      init() {
        if (this.initialized) return;
        this.initialized = true;

        // Open modal if errors or success message exist
        if (this.submited) {
          // The scratch/quiz steps are visible by default and the success
          // (or error) alert lives inside the email-offer step, which is
          // hidden by default in quiz mode. On the reload after a real
          // submit, jump straight to that step so the alert is what the
          // visitor actually sees instead of the scratch card again.
          const scratch = this.querySelector('[data-scratch-root]');
          const quiz = this.querySelector('[data-quiz-root]');
          const offer = this.querySelector('.newsletter-grid[data-quiz-offer]');
          if (scratch) scratch.hidden = true;
          if (quiz) quiz.hidden = true;
          if (offer) offer.hidden = false;

          this.load(1);
          return;
        }

        if (this.testMode || !this.getCookie(this.cookieName)) {
          this.load(this.delay);
        }
      }
    
      load(delay) {
        if (Shopify.designMode) return;
    
        setTimeout(() => this.show(), delay * 1000);
      }
    
      afterShow() {
        super.afterShow();
        this.classList.add('show-image');
      }
    
      afterHide() {
        super.afterHide();
        this.classList.remove('show-image');
    
        // Remove a cookie in case it was set in test mode
        if (this.testMode) {
          this.removeCookie(this.cookieName);
          return;
        }
    
        // Longer expiry after a real signup (page reloads with the success
        // alert), shorter expiry if they just closed it without signing up.
        const expiry = this.submited ? this.expiry : this.closeExpiry;
        this.setCookie(this.cookieName, expiry);
      }
    
      getCookie(name) {
        const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
        return match ? match[2] : null;
      }
    
      setCookie(name, expiry) {
        document.cookie = `${name}=true; max-age=${(expiry * 24 * 60 * 60)}; path=/`;
      }
    
      removeCookie(name) {
        document.cookie = `${name}=; max-age=0`;
      }
    }
  );
}

// Scratch-to-reveal card: gates the segmentation quiz behind a scratch reveal.
(() => {
  function setupScratchCard(root) {
    const canvas = root.querySelector('[data-scratch-canvas]');
    if (!canvas || canvas.dataset.scratchReady) return;
    canvas.dataset.scratchReady = 'true';

    // Use the canvas's own logical width/height attributes (already fixed by
    // markup/CSS at 280x150) rather than a live-measured rect — the modal's
    // open animation means layout can report a stale or zero-size rect for a
    // while after `hidden` is removed, which made this init silently no-op.
    const logicalWidth = canvas.width;
    const logicalHeight = canvas.height;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = logicalWidth * dpr;
    canvas.height = logicalHeight * dpr;
    ctx.scale(dpr, dpr);

    const halloween = document.documentElement.classList.contains('pp-halloween');

    if (halloween) {
      // Halloween cover: pumpkin-orange foil with a cobweb in two corners.
      const orange = ctx.createLinearGradient(0, 0, logicalWidth, logicalHeight);
      orange.addColorStop(0, '#ffa23a');
      orange.addColorStop(0.55, '#ff7a1a');
      orange.addColorStop(1, '#d9560a');
      ctx.fillStyle = orange;
      ctx.fillRect(0, 0, logicalWidth, logicalHeight);

      const drawWeb = (originX, originY, radius, start) => {
        ctx.strokeStyle = 'rgba(42, 15, 58, 0.5)';
        ctx.lineWidth = 1.2;
        const angles = [0, 18, 36, 54, 72, 90].map((deg) => ((deg + start) * Math.PI) / 180);
        angles.forEach((angle) => {
          ctx.beginPath();
          ctx.moveTo(originX, originY);
          ctx.lineTo(originX + radius * Math.cos(angle), originY + radius * Math.sin(angle));
          ctx.stroke();
        });
        [0.25, 0.5, 0.75, 1].forEach((step) => {
          for (let i = 0; i < angles.length - 1; i += 1) {
            const r = radius * step;
            const mid = (angles[i] + angles[i + 1]) / 2;
            ctx.beginPath();
            ctx.moveTo(originX + r * Math.cos(angles[i]), originY + r * Math.sin(angles[i]));
            ctx.quadraticCurveTo(
              originX + r * 0.82 * Math.cos(mid),
              originY + r * 0.82 * Math.sin(mid),
              originX + r * Math.cos(angles[i + 1]),
              originY + r * Math.sin(angles[i + 1])
            );
            ctx.stroke();
          }
        });
      };
      drawWeb(0, 0, 86, 0);
      drawWeb(logicalWidth, logicalHeight, 86, 180);

      ctx.fillStyle = '#2a0f3a';
      ctx.font = `900 ${Math.round(logicalWidth / 16)}px Montserrat, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('SCRATCH IF YOU DARE', logicalWidth / 2, logicalHeight / 2);
    } else {
    // Gold gradient base (brand swatch #C6A358) + a diagonal shine sweep + scattered glitter flecks.
    const gradient = ctx.createLinearGradient(0, 0, logicalWidth, logicalHeight);
    gradient.addColorStop(0, '#e6c98c');
    gradient.addColorStop(0.5, '#c6a358');
    gradient.addColorStop(1, '#9c7f42');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, logicalWidth, logicalHeight);

    // Soft diagonal highlight band for a shiny, foil-like sheen.
    const shine = ctx.createLinearGradient(0, logicalHeight, logicalWidth, 0);
    shine.addColorStop(0, 'rgba(255, 255, 255, 0)');
    shine.addColorStop(0.45, 'rgba(255, 255, 255, 0)');
    shine.addColorStop(0.55, 'rgba(255, 255, 255, 0.35)');
    shine.addColorStop(0.65, 'rgba(255, 255, 255, 0)');
    shine.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = shine;
    ctx.fillRect(0, 0, logicalWidth, logicalHeight);

    let seed = 42;
    const random = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };
    for (let i = 0; i < 220; i += 1) {
      const x = random() * logicalWidth;
      const y = random() * logicalHeight;
      const radius = random() * 1.3 + 0.3;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 255, 255, ${0.3 + random() * 0.65})`;
      ctx.fill();
    }

    ctx.fillStyle = '#fdfbf3';
    ctx.font = `800 ${Math.round(logicalWidth / 17)}px Montserrat, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(58, 36, 5, 0.45)';
    ctx.shadowBlur = 4;
    ctx.fillText('SCRATCH HERE', logicalWidth / 2, logicalHeight / 2);
    ctx.shadowBlur = 0;
    }
    ctx.globalCompositeOperation = 'destination-out';

    let scratching = false;
    let revealed = false;

    const pointFromEvent = (event) => {
      const bounds = canvas.getBoundingClientRect();
      const point = event.touches ? event.touches[0] : event;
      return { x: point.clientX - bounds.left, y: point.clientY - bounds.top };
    };

    const scratchAt = (x, y) => {
      ctx.beginPath();
      ctx.arc(x, y, 18, 0, Math.PI * 2);
      ctx.fill();
      if (window.ppHalloweenSound) window.ppHalloweenSound.scratch(x, y);
    };

    const checkRevealed = () => {
      if (revealed) return;
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      let cleared = 0;
      let sampled = 0;
      for (let i = 3; i < data.length; i += 4 * 15) {
        sampled += 1;
        if (data[i] === 0) cleared += 1;
      }
      if (sampled > 0 && cleared / sampled > 0.45) {
        revealed = true;
        revealScratchCard(root);
      }
    };

    const start = (event) => {
      scratching = true;
      const p = pointFromEvent(event);
      scratchAt(p.x, p.y);
    };
    const move = (event) => {
      if (!scratching) return;
      const p = pointFromEvent(event);
      scratchAt(p.x, p.y);
      checkRevealed();
    };
    const stop = () => {
      scratching = false;
    };

    canvas.addEventListener('pointerdown', start);
    canvas.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
  }

  function revealScratchCard(root) {
    const canvas = root.querySelector('[data-scratch-canvas]');
    const hint = root.querySelector('[data-scratch-hint]');
    if (canvas) {
      canvas.style.transition = 'opacity 400ms ease';
      canvas.style.opacity = '0';
    }
    if (hint) hint.textContent = hint.dataset.unlockedText || 'Unlocked! Answer one question to claim it.';
    if (window.ppHalloweenSound) window.ppHalloweenSound.reveal();

    setTimeout(() => {
      const quizPanel = document.querySelector('[data-quiz-root]');
      root.hidden = true;
      if (quizPanel) quizPanel.hidden = false;
    }, 900);
  }

  // Canvas setup no longer depends on live layout (see setupScratchCard
  // above — it draws at its fixed logical size, not a measured rect), and
  // centering is now handled declaratively in CSS via a static position +
  // flex parent (see newsletter-popup.css), which the drawer's animation
  // can't interfere with. So this can just run once, immediately — no
  // polling or waiting on the modal's open state required.
  document.querySelectorAll('[data-scratch-root]').forEach(setupScratchCard);
})();

// Segmentation quiz: pick-a-goal step before the offer + email form.
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-quiz-tag]');
  if (!button) return;

  const quizPanel = document.querySelector('[data-quiz-root]');
  const offerPanel = document.querySelector('[data-quiz-offer]');
  const headingEl = document.querySelector('[data-quiz-offer-heading]');
  const textEl = document.querySelector('[data-quiz-offer-text]');
  const tagsInput = offerPanel ? offerPanel.querySelector('input[name="contact[tags]"]') : null;

  if (headingEl) headingEl.textContent = button.dataset.quizHeading || '';
  if (textEl) textEl.textContent = button.dataset.quizText || '';
  if (tagsInput) {
    const baseTags = tagsInput.defaultValue || 'newsletter';
    tagsInput.value = `${baseTags},quiz-${button.dataset.quizTag}`;
  }

  if (quizPanel) quizPanel.hidden = true;
  if (offerPanel) offerPanel.hidden = false;
});

// Popup signups go straight to Klaviyo (the list the welcome flow is
// triggered by) via Klaviyo's public client API, so the popup stays open on
// the same step and shows a real success or error without reloading.
// Klaviyo's "Sync profiles to Shopify" setting then creates the Shopify
// customer. The popup deliberately doesn't post Shopify's /contact form:
// Shopify's bot protection (hCaptcha + /challenge) blocks background posts
// on the live store, and binding hCaptcha shows its badge on every page.
// The form is marked data-nocaptcha so Shopify never binds it.
function getKlaviyoConfig(form) {
  const modal = form.closest('newsletter-modal');
  const companyId = modal ? modal.dataset.klaviyoCompanyId : '';
  const listId = modal ? modal.dataset.klaviyoListId : '';
  return companyId && listId ? { companyId, listId } : null;
}

function subscribeToKlaviyo(form, config) {
  const emailInput = form.querySelector('input[name="contact[email]"]');
  const tagsInput = form.querySelector('input[name="contact[tags]"]');
  const quizMatch = tagsInput ? tagsInput.value.match(/quiz-([\w-]+)/) : null;

  const properties = { signup_source: 'Scratch quiz popup' };
  if (quizMatch) properties.quiz_goal = quizMatch[1];

  return fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(config.companyId)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/vnd.api+json',
      Accept: 'application/vnd.api+json',
      revision: '2025-07-15',
    },
    body: JSON.stringify({
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Scratch quiz popup',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: emailInput ? emailInput.value : '',
                properties,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
              },
            },
          },
        },
        relationships: {
          list: { data: { type: 'list', id: config.listId } },
        },
      },
    }),
  }).then((response) => {
    if (!response.ok) throw new Error(`Klaviyo subscribe failed (${response.status})`);
  });
}

document.addEventListener('submit', (event) => {
  const form = event.target.closest('.newsletter-modal .newsletter-form');
  if (!form) return;

  // Without Klaviyo configured, let the normal Shopify form submit happen.
  const klaviyo = getKlaviyoConfig(form);
  if (!klaviyo) return;

  event.preventDefault();
  if (form.dataset.pp7Submitting === 'true') return;
  form.dataset.pp7Submitting = 'true';

  const submitButton = form.querySelector('button[type="submit"]');
  if (submitButton) submitButton.disabled = true;

  const emailInput = form.querySelector('input[name="contact[email]"]');
  const email = emailInput ? emailInput.value : '';
  subscribeToKlaviyo(form, klaviyo)
    .then(() => showPopupSuccess(form, email))
    .catch((error) => {
      console.error(error);
      showPopupError(form, 'Something went wrong. Please try again.');
    });
});

function showPopupError(form, text) {
  form.dataset.pp7Submitting = 'false';
  const submitButton = form.querySelector('button[type="submit"]');
  if (submitButton) submitButton.disabled = false;

  let alert = form.querySelector('[data-pp7-popup-error]');
  if (!alert) {
    alert = document.createElement('div');
    alert.className = 'alert alert--error flex items-start gap-3 text-sm md:text-base leading-tight';
    alert.setAttribute('role', 'alert');
    alert.setAttribute('data-pp7-popup-error', '');
    form.appendChild(alert);
  }
  alert.textContent = text;
}

function showPopupSuccess(form, email) {
  form.hidden = true;

  // The "enter your email…" prompt text blocks no longer apply once they have.
  const richText = form.closest('.rich-text');
  if (richText) {
    richText.querySelectorAll(':scope > .rte').forEach((el) => {
      el.hidden = true;
    });
  }

  const message = document.createElement('div');
  message.className = 'alert alert--success flex items-start gap-3 text-sm md:text-base leading-tight';
  message.setAttribute('role', 'status');

  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  icon.setAttribute('viewBox', '0 0 24 24');
  icon.setAttribute('width', '20');
  icon.setAttribute('height', '20');
  icon.setAttribute('fill', 'none');
  icon.setAttribute('stroke', 'currentColor');
  icon.setAttribute('stroke-width', '2');
  icon.classList.add('shrink-0');
  icon.innerHTML = '<circle cx="12" cy="12" r="10"></circle><path d="m9 12 2 2 4-4" stroke-linecap="round" stroke-linejoin="round"></path>';

  // The code itself comes from the Klaviyo welcome flow (triggered by the
  // quiz-* Shopify tag this signup carries), not from the theme.
  const text = document.createElement('span');
  text.textContent = "You're in! Check your inbox for your code.";

  message.append(icon, text);
  form.insertAdjacentElement('afterend', message);

  const modal = form.closest('newsletter-modal');
  setTimeout(() => {
    if (modal) modal.hide();
  }, 5000);
}
